import ApplicationController from "application_controller.js";

//* Plays the animated rendition of the .bg-main scene, swapping between the
//* day and night videos when the color mode changes. The element stays hidden
//* (opacity 0) until playback actually starts, so the static image remains the
//* first paint and the only background whenever the video cannot play.
export default class extends ApplicationController {

  debug = false;

  static values = {
    dayUrl: String,
    nightUrl: String,
  };

  //* Matches media-breakpoint-up(md), where .bg-main becomes a fixed cover.
  //* Below it the static image scrolls with the page, so the fixed video would not line up.
  static MIN_WIDTH_QUERY = "(min-width: 768px)";

  connect() {
    this.log_function("connect");

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (navigator.connection && navigator.connection.saveData) return;
    if (!window.matchMedia(this.constructor.MIN_WIDTH_QUERY).matches) return;

    this.boundShow = this.show.bind(this);
    this.boundHide = this.hide.bind(this);
    this.boundHandleVisibility = this.handleVisibility.bind(this);
    this.boundApplySource = this.applySource.bind(this);

    this.element.addEventListener("playing", this.boundShow);
    this.element.addEventListener("error", this.boundHide);
    document.addEventListener("visibilitychange", this.boundHandleVisibility);
    window.addEventListener("background-video:toggle", this.boundApplySource);

    this.themeObserver = new MutationObserver(() => this.applySource());
    this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-bs-theme"] });

    this.applySource();
  }

  disconnect() {
    this.log_function("disconnect");

    if (this.themeObserver) this.themeObserver.disconnect();
    this.element.removeEventListener("playing", this.boundShow);
    this.element.removeEventListener("error", this.boundHide);
    document.removeEventListener("visibilitychange", this.boundHandleVisibility);
    window.removeEventListener("background-video:toggle", this.boundApplySource);
  }

  applySource() {
    this.log_function("applySource");

    if (this.disabled) {
      this.stop();
      return;
    }

    let url;

    switch (this.currentTheme()) {
      case "dark":
        url = this.nightUrlValue;
        break;
      default:
        url = this.dayUrlValue;
        break;
    }

    //* Same source: just make sure playback runs again (e.g. after re-enabling)
    if (this.element.getAttribute("src") === url) {
      this.element.play().catch(() => this.hide());
      return;
    }

    this.hide();
    this.element.src = url;
    this.element.play().catch(() => this.hide());
  }

  stop() {
    this.element.pause();
    this.hide();
  }

  //* The color-mode controller keeps this attribute in sync with the stored preference
  currentTheme() {
    return document.documentElement.getAttribute("data-bs-theme");
  }

  //* The background-video-toggle controller owns this cookie
  get disabled() {
    return /(?:^|;\s*)background_video=off(?:;|$)/.test(document.cookie);
  }

  show() {
    this.element.classList.add("is-playing");
  }

  hide() {
    this.element.classList.remove("is-playing");
  }

  //* Saves cycles while the tab is in the background
  handleVisibility() {
    if (document.hidden) {
      this.element.pause();
    } else {
      this.element.play().catch(() => {});
    }
  }
}
